/**
 * RecapLetter — a slim wrapped letter resting on the Bookshelf.
 *
 * Visually distinct from chapter spines per the brand:
 *   • cream/linen/vellum cloth (warm light tones)
 *   • a single wrapped ribbon across the spine (gold/cream/rose)
 *   • a tiny wax-style seal where the ribbon crosses
 *   • a handwritten label band near the top with "✦"
 *
 * The letter reads as PRESERVED — not as a piece of mail. Tapping
 * "pulls" the letter from the shelf and opens an in-page reading
 * overlay. NEVER "View email" / "Open recap" — language stays
 * archival.
 */
import React, { useMemo } from 'react';

const CLOTH = {
  cream:  'linear-gradient(180deg, #F4ECD9 0%, #E2D5B6 100%)',
  linen:  'linear-gradient(180deg, #EDE3CC 0%, #D7C8A6 100%)',
  vellum: 'linear-gradient(180deg, #EBDFC4 0%, #C9B690 100%)',
};
const RIBBON = {
  gold:  { color: '#B8923F', shadow: 'rgba(184,146,63,0.45)' },
  cream: { color: '#A48D5F', shadow: 'rgba(164,141,95,0.40)' },
  rose:  { color: '#A85F60', shadow: 'rgba(168,95,96,0.45)' },
};

export default function RecapLetter({
  letter,
  shelfHeight = 360,
  onPull,
  entranceDelay = 0,
  focused = false,
}) {
  const spine = letter?.spine || {};
  const cloth = spine.cloth || 'cream';
  const ribbon = RIBBON[spine.ribbon] || RIBBON.gold;
  const height = Math.round((spine.height_pct || 0.74) * shelfHeight);
  const thickness = Math.max(14, Math.min(28, spine.thickness_px || 18));
  const lean = spine.lean_deg || 0;

  const focusedShadow = focused ? `, 0 0 24px 3px ${ribbon.shadow}, 0 0 0 1px ${ribbon.color}88` : '';
  const focusedAnim = focused ? `, spineFocusGlow 2.6s ease-out ${entranceDelay + 200}ms 3 both` : '';

  const style = useMemo(() => ({
    position: 'relative',
    width: thickness,
    height,
    flex: `0 0 ${thickness}px`,
    background: CLOTH[cloth] || CLOTH.cream,
    boxShadow: `
      0 8px 18px rgba(0,0,0,0.40),
      0 0 0 1px rgba(60,40,20,0.18),
      inset 0 14px 18px rgba(150,120,80,0.18),
      inset 0 -10px 14px rgba(120,90,50,0.20)
      ${focusedShadow}
    `,
    transform: `rotate(${lean}deg)`,
    transformOrigin: 'bottom center',
    transition: 'transform 480ms cubic-bezier(0.22, 0.61, 0.36, 1), filter 480ms ease',
    cursor: 'pointer',
    borderRadius: '2px',
    overflow: 'hidden',
    animation: `spineEnter 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${entranceDelay}ms both, letterBreathe 9s ease-in-out ${entranceDelay + 800}ms infinite${focusedAnim}`,
  }), [thickness, height, cloth, lean, entranceDelay, ribbon, focused, focusedShadow, focusedAnim]);

  return (
    <button
      type="button"
      onClick={() => onPull && onPull(letter)}
      data-testid={`shelf-letter-${letter.id}`}
      aria-label="Revisit the letter from this night"
      style={{
        background: 'transparent', border: 'none', padding: 0, margin: 0,
        display: 'inline-block',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget.querySelector('[data-letter]');
        if (el) {
          el.style.transform = `rotate(${lean * 0.5}deg) translateY(-3px)`;
          el.style.filter = 'brightness(1.04)';
        }
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget.querySelector('[data-letter]');
        if (el) {
          el.style.transform = `rotate(${lean}deg)`;
          el.style.filter = 'none';
        }
      }}
    >
      <div data-letter style={style}>
        {/* Handwritten label band — a small cream band near the top with ✦ */}
        <div style={{
          position: 'absolute',
          top: '14%', left: -1, right: -1, height: 22,
          background: 'linear-gradient(180deg, rgba(255,250,240,0.92) 0%, rgba(245,235,215,0.88) 100%)',
          borderTop: '1px solid rgba(80,55,25,0.18)',
          borderBottom: '1px solid rgba(80,55,25,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Georgia, serif',
          fontSize: 9,
          color: '#7A5A2E',
          letterSpacing: '0.12em',
        }}>✦</div>

        {/* Ribbon — vertical strap across the spine */}
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: '50%', transform: 'translateX(-50%)',
          width: Math.max(4, Math.floor(thickness * 0.32)),
          background: `linear-gradient(180deg, ${ribbon.color} 0%, ${ribbon.color}dd 100%)`,
          boxShadow: `0 0 6px ${ribbon.shadow}, inset 0 0 0 1px rgba(0,0,0,0.18)`,
        }} />

        {/* Wax-style seal where the ribbon crosses ~ 60% down */}
        <div style={{
          position: 'absolute',
          top: '58%', left: '50%',
          width: Math.max(7, Math.floor(thickness * 0.55)),
          height: Math.max(7, Math.floor(thickness * 0.55)),
          transform: 'translate(-50%, -50%) rotate(-12deg)',
          background: 'radial-gradient(circle at 35% 30%, #C2434C 0%, #7A1F26 80%)',
          borderRadius: '50%',
          boxShadow: '0 1px 3px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.20)',
        }} />
      </div>
    </button>
  );
}
